import React, { useState, useEffect } from "react";
import "./style.css";

// MUI imports
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  Tooltip,
} from "@mui/material";

import {
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CompareArrows as CompareArrowsIcon,
  Inventory2Outlined as InventoryIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarningIcon from "@mui/icons-material/Warning";

const GILROY_MEDIUM = "gilroyMedium";
const GILROY_BOLD = "GilroyBold";

// ---------- helpers ----------
const normalize = (text) =>
  (text ?? "").toString().replace(/\s+/g, " ").trim().toLowerCase();

const clean = (s) =>
  normalize(s)
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const toDash = (v) =>
  v === undefined || v === null || String(v).trim() === "" ? "-" : v;

const pick = (obj, keys) => {
  for (const k of keys) {
    if (
      obj &&
      obj[k] !== undefined &&
      obj[k] !== null &&
      String(obj[k]).trim() !== ""
    ) {
      return obj[k];
    }
  }
  return undefined;
};

const parseMoney = (s) => {
  if (s === null || s === undefined) return null;
  if (typeof s === "number") return Number.isFinite(s) ? s : null;

  const str = String(s).trim();
  if (!str) return null;

  // Keep only digits, minus and dot so we can parse values like:
  // "â‚¹4,89,000", "489,000.00", "â‚¹12,31,850", etc.
  const normalized = str.replace(/[^\d.-]+/g, "");
  if (
    !normalized ||
    normalized === "-" ||
    normalized === "." ||
    normalized === "-."
  ) {
    return null;
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
};

const buildStopwords = (poItems, grnItems, invoiceItems) => {
  const freq = {};
  const allTexts = [
    ...poItems.map(
      (x) => x.description || x.description_of_goods || x.item_name || ""
    ),
    ...grnItems.map((x) => x.item_name || x.description || ""),
    ...invoiceItems.map(
      (x) => x.item_name || x.item_description || x.description || ""
    ),
  ];

  // For very small datasets, stopwording becomes too aggressive and breaks matching.
  // Disable stopwords to avoid generating duplicate/extra line items in the UI.
  if (allTexts.length < 10) return new Set();

  allTexts.forEach((txt) => {
    clean(txt)
      .split(" ")
      .filter(Boolean)
      .forEach((w) => (freq[w] = (freq[w] || 0) + 1));
  });
  const total = Math.max(allTexts.length, 1);
  return new Set(Object.keys(freq).filter((w) => freq[w] / total > 0.6));
};

const keywordsFrom = (s, stopwords) =>
  clean(s)
    .split(" ")
    .filter((w) => w.length > 1 && !stopwords.has(w));

const sameByText = (left, right, stopwords) => {
  const A = new Set(keywordsFrom(left, stopwords));
  const B = new Set(keywordsFrom(right, stopwords));
  let common = 0;
  A.forEach((t) => B.has(t) && common++);
  return common >= 2 || (common >= 1 && (A.size <= 2 || B.size <= 2));
};

// Status Chip Component for modal
const ModalStatusChip = ({ status, type }) => {
  const config = {
    matched: {
      color: "#2E7D32",
      bgcolor: "#E8F5E9",
      icon: <CheckCircleIcon style={{ fontSize: 14 }} />,
    },
    partial: {
      color: "#ED6C02",
      bgcolor: "#FFF3E0",
      icon: <WarningIcon style={{ fontSize: 14 }} />,
    },
    rejected: {
      color: "#D32F2F",
      bgcolor: "#FFEBEE",
      icon: <CancelIcon style={{ fontSize: 14 }} />,
    },
    default: { color: "#757575", bgcolor: "#FAFAFA", icon: "?" },
  };

  const { color, bgcolor, icon } = config[type] || config.default;

  return (
    <Chip
      icon={icon}
      label={status}
      size="small"
      sx={{
        fontFamily: GILROY_MEDIUM,
        fontSize: "14px",
        fontWeight: 600,
        height: "30px",
        backgroundColor: bgcolor,
        color: color,
        border: `1px solid ${color}30`,
        "& .MuiChip-label": {
          px: 0.5,
        },
        "& .MuiChip-icon": {
          color: color,
          fontSize: "14px",
        },
      }}
    />
  );
};

// ---------- Component ----------
const ThreeWayMatchTable = ({
  poData,
  invoiceData,
  grnData,
  open,
  onClose,
  showSummary,
  selectedCard,
}) => {
  const [modalData, setModalData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const isExternallyOpen = Boolean(open);

  const closeDialog = () => {
    if (typeof onClose === "function") onClose();
    setShowModal(false);
  };

  // Helper functions for status analysis
  // 1. Item-Level Quantity Status
  const getQuantityStatusForItem = (poQty, grnQty, invQty) => {
    poQty = Number(poQty) || 0;
    grnQty = Number(grnQty) || 0;
    invQty = Number(invQty) || 0;

    if (poQty === 0 && grnQty === 0 && invQty === 0) {
      return { status: "No Data", type: "partial" };
    }

    // ðŸ”´ REJECTED: Quantity EXCEEDING (greater than)
    if (grnQty > poQty || invQty > poQty) {
      return { status: "Exceeding", type: "rejected" };
    }

    // ðŸŸ¡ PARTIAL: Quantity LESS (smaller than)
    if (grnQty < poQty || invQty < poQty) {
      return { status: "Partial", type: "partial" };
    }

    // âœ… MATCHED: Exact match
    if (poQty === grnQty && poQty === invQty) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "partial" };
  };

  // 2. Item-Level Price Status
  const getPriceStatusForItem = (poPrice, invPrice) => {
    poPrice = Number(poPrice) || 0;
    invPrice = Number(invPrice) || 0;

    if (poPrice === 0 && invPrice === 0) {
      return { status: "No Data", type: "partial" };
    }

    // ðŸ”´ REJECTED: Price INCREASED (greater than)
    if (invPrice > poPrice) {
      return { status: "Increased", type: "rejected" };
    }

    // ðŸŸ¡ PARTIAL: Price DECREASED (smaller than)
    if (invPrice < poPrice) {
      return { status: "Partial", type: "partial" };
    }

    // âœ… MATCHED: Same price
    if (poPrice === invPrice) {
      return { status: "Matched", type: "matched" };
    }

    return { status: "No Match", type: "partial" };
  };

  // 3. Item-Level Item Status
  const getItemStatusForRow = (row, poItems, grnItems, invItems) => {
    // Check if this specific item exists in all documents

    const existsInPO = poItems.some(
      (item) =>
        (item.part_no || "").includes(row.partNo) ||
        (item.description || "").includes(row.item)
    );

    const existsInGRN = grnItems.some(
      (item) =>
        (item.part_no || "").includes(row.partNo) ||
        (item.item_name || "").includes(row.item)
    );

    const existsInInvoice = invItems.some(
      (item) =>
        (item.part_no || "").includes(row.partNo) ||
        (item.item_name || "").includes(row.item) ||
        (item.description || "").includes(row.item) ||
        (item.item_description || "").includes(row.item) ||
        (item.description_of_goods || "").includes(row.item)
    );

    // ðŸ”´ REJECTED: Extra item (not in PO but in GRN/Invoice)
    if (!existsInPO) {
      return { status: "Extra Item", type: "rejected" };
    }

    // ðŸŸ¡ PARTIAL: Missing from GRN or Invoice
    if (!existsInGRN || !existsInInvoice) {
      return { status: "Missing", type: "partial" };
    }

    // âœ… MATCHED: Exists in all three
    return { status: "Matched", type: "matched" };
  };

  const buildMergedRows = (poItems, grnItems, invoiceItems, STOPWORDS) => {
    const usedGrnIndexes = new Set();
    const usedInvIndexes = new Set();

    const findPoByCodeOrText = (partNo, itemCode, desc) =>
      poItems.find(
        (p) =>
          clean(pick(p, ["part_no", "partno", "item_code", "itemcode"]) || "") ===
            clean(partNo || itemCode || "") ||
          clean(pick(p, ["part_no", "partno", "item_code", "itemcode"]) || "") ===
            clean(itemCode || partNo || "")
      ) ||
      poItems.find((p) =>
        sameByText(
          desc || "",
          pick(p, ["description_of_goods", "description", "item_name"]) || "",
          STOPWORDS
        )
      );

    const findGrnByCodeOrText = (partNo, itemCode, desc) => {
      const codeA = clean(partNo || itemCode || "");
      const codeB = clean(itemCode || partNo || "");

      const exactIdx = grnItems.findIndex((g, idx) => {
        if (usedGrnIndexes.has(idx)) return false;
        const code = clean(pick(g, ["item_code", "part_no", "partno"]) || "");
        return code && (code === codeA || code === codeB);
      });

      if (exactIdx >= 0) {
        usedGrnIndexes.add(exactIdx);
        return grnItems[exactIdx];
      }

      const candidates = [];
      grnItems.forEach((g, idx) => {
        if (usedGrnIndexes.has(idx)) return;
        const gName =
          pick(g, ["item_name", "description", "description_of_goods"]) || "";
        if (!gName) return;
        if (sameByText(desc || "", gName, STOPWORDS)) {
          candidates.push({ idx, g });
        }
      });

      if (candidates.length === 0) return undefined;
      if (candidates.length === 1) {
        usedGrnIndexes.add(candidates[0].idx);
        return candidates[0].g;
      }

      const poAmount = parseMoney(
        pick(findPoByCodeOrText(partNo, itemCode, desc) || {}, [
          "parsed_amount",
          "amount",
          "total",
          "line_total",
        ])
      );

      candidates.sort((a, b) => {
        if (poAmount == null) return a.idx - b.idx;
        const aAmt =
          parseMoney(pick(a.g || {}, ["total_price", "total_amount", "amount"])) ??
          0;
        const bAmt =
          parseMoney(pick(b.g || {}, ["total_price", "total_amount", "amount"])) ??
          0;
        return Math.abs(aAmt - poAmount) - Math.abs(bAmt - poAmount);
      });

      usedGrnIndexes.add(candidates[0].idx);
      return candidates[0].g;
    };

    const findInvByCodeOrText = (partNo, itemCode, desc, grnRef) => {
      const codeA = clean(partNo || "");
      const codeB = clean(itemCode || "");

      const exactIdx = invoiceItems.findIndex((inv, idx) => {
        if (usedInvIndexes.has(idx)) return false;
        const code = clean(pick(inv, ["item_code", "part_no", "partno"]) || "");
        return code && (code === codeA || code === codeB);
      });

      if (exactIdx >= 0) {
        usedInvIndexes.add(exactIdx);
        return invoiceItems[exactIdx];
      }

      const needle = desc || pick(grnRef || {}, ["item_name"]) || "";
      const textCandidates = [];
      invoiceItems.forEach((inv, idx) => {
        if (usedInvIndexes.has(idx)) return;
        const hay =
          pick(inv, ["item_name", "description", "item_description", "particulars"]) ||
          "";
        if (!hay) return;
        if (sameByText(needle, hay, STOPWORDS)) {
          textCandidates.push({ idx, inv });
        }
      });

      let invoice = undefined;
      if (textCandidates.length === 1) {
        invoice = textCandidates[0].inv;
        usedInvIndexes.add(textCandidates[0].idx);
      } else if (textCandidates.length > 1) {
        const poAmount = parseMoney(
          pick(findPoByCodeOrText(partNo, itemCode, desc) || {}, [
            "parsed_amount",
            "amount",
            "total",
            "line_total",
          ])
        );

        textCandidates.sort((a, b) => {
          if (poAmount == null) return a.idx - b.idx;
          const aAmt =
            parseMoney(
              pick(a.inv || {}, ["parsed_amount", "amount", "line_total", "total"])
            ) ?? 0;
          const bAmt =
            parseMoney(
              pick(b.inv || {}, ["parsed_amount", "amount", "line_total", "total"])
            ) ?? 0;
          return Math.abs(aAmt - poAmount) - Math.abs(bAmt - poAmount);
        });

        invoice = textCandidates[0].inv;
        usedInvIndexes.add(textCandidates[0].idx);
      }

      if (!invoice) {
        const poHSN = pick(
          findPoByCodeOrText(partNo, itemCode, desc) || {},
          ["hsn_code", "hsn"]
        );

        const fallbackIdx = invoiceItems.findIndex((inv, idx) => {
          if (usedInvIndexes.has(idx)) return false;
          const invHSN = pick(inv, ["hsn", "hsn_sac"]);
          const poRate = parseMoney(
            pick(findPoByCodeOrText(partNo, itemCode, desc) || {}, [
              "parsed_rate",
              "rate",
              "unit_price",
              "price",
            ])
          );
          const invRate = parseMoney(
            pick(inv, [
              "unit_rate",
              "rate",
              "unit_price",
              "price",
              "parsed_rate",
            ])
          );
          const rateClose =
            poRate != null &&
            invRate != null &&
            Math.abs(poRate - invRate) < 0.01;
          return (
            (poHSN && invHSN && String(poHSN) === String(invHSN)) || rateClose
          );
        });

        if (fallbackIdx >= 0) {
          invoice = invoiceItems[fallbackIdx];
          usedInvIndexes.add(fallbackIdx);
        }
      }

      return invoice;
    };

    const buildRow = ({ partNo, itemCode, desc, poItem, grnItem, invItem }) => {
      const poQty =
        parseMoney(
          pick(poItem || {}, ["po_qty", "qty.", "qty", "quantity", "po_quantity"])
        ) || 0;

      const grnQty =
        parseMoney(
          pick(grnItem || {}, [
            "received_q",
            "received_qty",
            "quantity_received",
            "mrn_qty",
            "qty",
            "unt",
            "received_quantity",
            "received_qty.",
          ])
        ) || 0;

      const invQty =
        parseMoney(
          pick(invItem || {}, [
            "order_quantity",
            "quantity",
            "qty",
            "invoice_qty",
            "parsed_qty",
            "parsed_quantity",
            "unt",
          ])
        ) || 0;

      const unitPricePO =
        parseMoney(
          pick(poItem || {}, ["parsed_rate", "rate", "unit_price", "price"])
        ) || 0;
      const unitPriceInvoice =
        parseMoney(
          pick(invItem || {}, [
            "unit_rate",
            "unit_price",
            "rate",
            "price",
            "parsed_rate",
          ])
        ) || 0;

      let totalPricePO = parseMoney(
        pick(poItem || {}, ["parsed_amount", "amount", "total", "line_total"])
      );
      if (totalPricePO === undefined || totalPricePO === null) {
        totalPricePO = poQty * unitPricePO;
      }

      let totalPriceInvoice = parseMoney(
        pick(invItem || {}, ["parsed_amount", "line_total", "amount", "total"])
      );
      if (totalPriceInvoice === undefined || totalPriceInvoice === null) {
        totalPriceInvoice = invQty * unitPriceInvoice;
      }

      const poVsGrnQtyVariance = grnQty - poQty;
      const grnVsInvQtyVariance = grnQty - invQty;
      const priceVariance = totalPricePO - totalPriceInvoice;

      const poVsGrnQtyVarianceType =
        grnQty > poQty ? "GRN > PO" : grnQty < poQty ? "GRN < PO" : "Equal";

      const grnVsInvQtyVarianceType =
        grnQty > invQty
          ? "GRN > Invoice"
          : grnQty < invQty
          ? "GRN < Invoice"
          : "Equal";

      const priceVarianceType =
        totalPricePO > totalPriceInvoice
          ? "PO Total > Invoice Total"
          : totalPricePO < totalPriceInvoice
          ? "PO Total < Invoice Total"
          : "Equal";

      const quantityStatus = getQuantityStatusForItem(poQty, grnQty, invQty);
      const priceStatus = getPriceStatusForItem(
        unitPricePO,
        unitPriceInvoice,
        totalPricePO,
        totalPriceInvoice
      );
      const itemStatus = getItemStatusForRow(
        {
          partNo: partNo || "",
          item: desc || "",
        },
        poItems,
        grnItems,
        invoiceItems
      );

      return {
        item: desc || "-",
        partNo: toDash(partNo),
        itemCode: toDash(itemCode),
        poQty: poQty,
        grnQty: grnQty,
        invoiceQty: invQty,
        unitPricePO: unitPricePO,
        unitPriceInvoice: unitPriceInvoice,
        totalPricePO: totalPricePO,
        totalPriceInvoice: totalPriceInvoice,
        poVsGrnQtyVariance: poVsGrnQtyVariance,
        grnVsInvQtyVariance: grnVsInvQtyVariance,
        priceVariance: priceVariance,
        poVsGrnQtyVarianceType: poVsGrnQtyVarianceType,
        grnVsInvQtyVarianceType: grnVsInvQtyVarianceType,
        priceVarianceType: priceVarianceType,
        quantityStatus: quantityStatus,
        priceStatus: priceStatus,
        itemStatus: itemStatus,
      };
    };

    const merged = poItems.map((poItem) => {
      const partNo = pick(poItem, [
        "part_no",
        "partno",
        "item_code",
        "itemcode",
      ]);
      const itemCode = pick(poItem, [
        "item_code",
        "itemcode",
        "part_no",
        "partno",
      ]);
      const poDesc =
        pick(poItem, ["description_of_goods", "description", "item_name"]) ||
        "";

      const grn = findGrnByCodeOrText(partNo, itemCode, poDesc);
      const invoice = findInvByCodeOrText(partNo, itemCode, poDesc, grn);

      return buildRow({
        partNo,
        itemCode,
        desc: poDesc,
        poItem,
        grnItem: grn,
        invItem: invoice,
      });
    });

    const seen = new Set(
      merged.map(
        (r) =>
          `${clean(r.partNo || r.itemCode || "")}|${clean(r.item || "")}|${r.invoiceQty}|${r.grnQty}`
      )
    );

    grnItems.forEach((grnItem) => {
      const partNo = pick(grnItem, ["part_no", "partno", "item_code", "code"]);
      const itemCode = pick(grnItem, [
        "item_code",
        "code",
        "part_no",
        "partno",
      ]);
      const desc =
        pick(grnItem, ["item_name", "description", "description_of_goods"]) ||
        "";
      const poItem = findPoByCodeOrText(partNo, itemCode, desc);
      if (poItem) return;

      const invItem = findInvByCodeOrText(partNo, itemCode, desc, grnItem);
      const row = buildRow({
        partNo,
        itemCode,
        desc,
        poItem: null,
        grnItem,
        invItem,
      });

      const rowKey = `${clean(row.partNo || row.itemCode || "")}|${clean(
        row.item || ""
      )}|${row.invoiceQty}|${row.grnQty}`;
      if (!seen.has(rowKey)) {
        seen.add(rowKey);
        merged.push(row);
      }
    });

    invoiceItems.forEach((invItem) => {
      const partNo = pick(invItem, ["part_no", "partno", "item_code", "code"]);
      const itemCode = pick(invItem, [
        "item_code",
        "code",
        "part_no",
        "partno",
      ]);
      const desc =
        pick(invItem, [
          "item_name",
          "description",
          "item_description",
          "particulars",
        ]) || "";
      const poItem = findPoByCodeOrText(partNo, itemCode, desc);
      if (poItem) return;
      const grnItem = findGrnByCodeOrText(partNo, itemCode, desc);
      const row = buildRow({
        partNo,
        itemCode,
        desc,
        poItem: null,
        grnItem,
        invItem,
      });

      const rowKey = `${clean(row.partNo || row.itemCode || "")}|${clean(
        row.item || ""
      )}|${row.invoiceQty}|${row.grnQty}`;
      if (!seen.has(rowKey)) {
        seen.add(rowKey);
        merged.push(row);
      }
    });

    return merged;
  };

  useEffect(() => {
    if (isExternallyOpen || showModal) {
      const poItems = poData?.[0]?.line_items || [];
      const grnItems = grnData?.[0]?.line_items || [];
      const invoiceItems = invoiceData?.[0]?.line_items || [];

      const STOPWORDS = buildStopwords(poItems, grnItems, invoiceItems);

      const merged = buildMergedRows(poItems, grnItems, invoiceItems, STOPWORDS);

      setModalData(merged);
    }
  }, [open, poData, grnData, invoiceData]);

  const formatNumber = (v) => {
    const n = parseMoney(v);
    if (n === 0) return "0.00";
    if (n == null) return "-";
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatVariance = (variance, type = "number") => {
    if (variance === 0) return "0";
    if (!variance) return "-";

    const absVariance = Math.abs(variance);
    const formattedValue =
      type === "number"
        ? absVariance.toLocaleString("en-IN", {
            maximumFractionDigits: 0,
          })
        : Math.round(absVariance).toString();

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {variance > 0 ? (
          <ArrowUpwardIcon sx={{ color: "#D84315", fontSize: 16, mr: 0.5 }} />
        ) : variance < 0 ? (
          <ArrowDownwardIcon sx={{ color: "#2E7D32", fontSize: 16, mr: 0.5 }} />
        ) : null}
        {formattedValue}
      </Box>
    );
  };

  const getVarianceTooltip = (row, type) => {
    if (type === "poVsGrnQty") {
      if (row.poVsGrnQtyVariance > 0) {
        return `GRN quantity (${row.grnQty}) is greater than PO quantity (${row.poQty})`;
      } else if (row.poVsGrnQtyVariance < 0) {
        return `GRN quantity (${row.grnQty}) is less than PO quantity (${row.poQty})`;
      } else {
        return "GRN and PO quantities match";
      }
    } else if (type === "grnVsInvQty") {
      if (row.grnVsInvQtyVariance > 0) {
        return `GRN quantity (${row.grnQty}) is greater than Invoice quantity (${row.invoiceQty})`;
      } else if (row.grnVsInvQtyVariance < 0) {
        return `GRN quantity (${row.grnQty}) is less than Invoice quantity (${row.invoiceQty})`;
      } else {
        return "GRN and Invoice quantities match";
      }
    } else {
      if (row.priceVariance > 0) {
        return `PO total price (${formatNumber(
          row.totalPricePO
        )}) is greater than Invoice total price (${formatNumber(
          row.totalPriceInvoice
        )})`;
      } else if (row.priceVariance < 0) {
        return `PO total price (${formatNumber(
          row.totalPricePO
        )}) is less than Invoice total price (${formatNumber(
          row.totalPriceInvoice
        )})`;
      } else {
        return "PO and Invoice total prices match";
      }
    }
  };

  const rawAddress = (() => {
    const addr = poData?.[0]?.shipping_address;

    if (!addr) return "No shipping address provided";

    if (typeof addr === "string") return addr;

    if (typeof addr === "object" && addr.address) return addr.address;

    return "No shipping address provided";
  })();

  const addressLines = rawAddress.split("\n");
  const stickySerialWidth = 72;
  const stickyDescriptionWidth = 260;
  const stickyPriceMatchWidth = 174;
  const stickyQuantityMatchWidth = 174;

  const toggleDescription = (rowIndex) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleViewItems = () => {
    const poItems = poData?.[0]?.line_items || [];
    const grnItems = grnData?.[0]?.line_items || [];
    const invoiceItems = invoiceData?.[0]?.line_items || [];

    const STOPWORDS = buildStopwords(poItems, grnItems, invoiceItems);

    const merged = buildMergedRows(poItems, grnItems, invoiceItems, STOPWORDS);

    setModalData(merged);
    setShowModal(true);
  };

  const summaryHeaderCellSx = {
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    color: "#0f172a",
    backgroundColor: "#E8F0F4",
    borderBottom: "1px solid #e2e8f0",
    py: 1.75,
    px: 2,
  };

  const summaryKeyCellSx = {
    fontWeight: 600,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    py: 2,
    px: 2,
    width: 240,
    minWidth: 240,
  };

  const summaryValueCellSx = {
    fontSize: "14px",
    fontFamily: GILROY_MEDIUM,
    color: "#0f172a",
    borderBottom: "1px solid #e2e8f0",
    py: 2,
    px: 2,
    verticalAlign: "middle",
  };

  const renderRow = (label, poValue, invoiceValue, grnValue) => (
    <TableRow>
      <TableCell sx={summaryKeyCellSx}>{label}</TableCell>
      <TableCell sx={{ ...summaryValueCellSx, width: 380, minWidth: 340 }}>
        {toDash(poValue)}
      </TableCell>
      <TableCell sx={{ ...summaryValueCellSx, width: 240, minWidth: 220 }}>
        {toDash(invoiceValue)}
      </TableCell>
      <TableCell sx={{ ...summaryValueCellSx, width: 240, minWidth: 220 }}>
        {toDash(grnValue)}
      </TableCell>
    </TableRow>
  );

  // Inline styles
  const headerCellStyle = {
    fontWeight: 700,
    fontSize: "14px",
    fontFamily: GILROY_BOLD,
    backgroundColor: "#e8eaf6",
    padding: "8px",
    borderRight: "1px solid #e0e0e0",
  };

  const cellStyle = {
    fontSize: "14px",
    fontFamily: GILROY_MEDIUM,
    padding: "8px",
    borderRight: "1px solid #f0f0f0",
  };

  const renderDescriptionCell = (value, rowIndex) => {
    const text = String(value || "-").trim() || "-";
    const isExpanded = Boolean(expandedDescriptions[rowIndex]);
    const isLongText = text.length > 72;

    return (
      <Box
        sx={{
          width: stickyDescriptionWidth - 24,
          maxWidth: stickyDescriptionWidth - 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
        }}
      >
        <Tooltip
          title={text === "-" ? "" : text}
          arrow
          placement="top-start"
          slotProps={{ tooltip: { sx: { fontSize: "12px", maxWidth: 420 } } }}
        >
          <Typography
            component="div"
            sx={{
              width: "100%",
              fontSize: "13px",
              lineHeight: 1.45,
              fontFamily: GILROY_MEDIUM,
              color: "#1e293b",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: isExpanded ? "unset" : 2,
              overflow: "hidden",
              whiteSpace: isExpanded ? "normal" : "initial",
              textOverflow: "ellipsis",
              wordBreak: "break-word",
            }}
          >
            {text}
          </Typography>
        </Tooltip>

        {isLongText ? (
          <Button
            size="small"
            variant="text"
            onClick={() => toggleDescription(rowIndex)}
            sx={{
              minWidth: "auto",
              padding: 0,
              fontSize: "11px",
              lineHeight: 1.2,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            {isExpanded ? "Read less" : "Read more"}
          </Button>
        ) : null}
      </Box>
    );
  };

  return (
    <div>
      {showSummary && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableBody>
              <TableRow sx={{ "& th": { fontWeight: 700 } }}>
                <TableCell sx={summaryHeaderCellSx}>
                  Key
                </TableCell>
                <TableCell sx={summaryHeaderCellSx}>
                  PO
                </TableCell>
                <TableCell sx={summaryHeaderCellSx}>
                  Invoice
                </TableCell>
                <TableCell sx={summaryHeaderCellSx}>
                  GRN
                </TableCell>
                <TableCell
                  rowSpan={6}
                  align="center"
                  sx={{
                    width: 170,
                    minWidth: 170,
                    verticalAlign: "middle",
                    px: 2,
                    borderLeft: "1px solid #e2e8f0",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Inventory2OutlinedIcon />}
                    onClick={handleViewItems}
                    sx={{
                      fontWeight: 600,
                      fontSize: "14px",
                      fontFamily: GILROY_MEDIUM,
                      minWidth: 124,
                    }}
                  >
                    View Items
                  </Button>
                </TableCell>
              </TableRow>
              {renderRow(
                "Purchase Order No",
                poData?.[0]?.purchase_order_no || poData?.[0]?.order_number,
                invoiceData?.[0]?.purchase_order_number,
                grnData?.[0]?.purchase_order_no || grnData?.[0]?.purchase_order_number
              )}
              {renderRow(
                "Invoice / Goods Receipt ID",
                "-",
                invoiceData?.[0]?.invoice_number,
                grnData?.[0]?.grn_number || grnData?.[0]?.grn_no
              )}
              {renderRow(
                "Supplier Name",
                poData?.[0]?.vendor,
                "-",
                grnData?.[0]?.vendor_name || grnData?.[0]?.vendor
              )}

              {renderRow(
                "Bill Date",
                poData?.[0]?.order_date,
                invoiceData?.[0]?.invoice_date,
                grnData?.[0]?.bill_date || grnData?.[0]?.date || grnData?.[0]?.grn_date
              )}

              {renderRow(
                "Total Amount",
                poData?.[0]?.total_amount != null
                  ? (parseMoney(poData[0].total_amount) ?? poData[0].total_amount).toLocaleString(
                      "en-IN"
                    )
                  : "-",
                invoiceData?.[0]?.invoice_amount != null
                  ? (parseMoney(invoiceData[0].invoice_amount) ?? invoiceData[0].invoice_amount).toLocaleString(
                      "en-IN"
                    )
                  : "-",
                grnData?.[0]?.total_amount != null
                  ? (parseMoney(grnData[0].total_amount) ?? grnData[0].total_amount).toLocaleString(
                      "en-IN"
                    )
                  : "-"
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Modal UI */}
      <Dialog
        open={Boolean(open) || showModal}
        onClose={closeDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          style: {
            borderRadius: "8px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#1976d2",
            color: "white",
            padding: "12px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <AssignmentIcon style={{ fontSize: 28 }} />
            <Typography
              variant="h4"
              style={{ fontWeight: 600, fontFamily: GILROY_BOLD, fontSize: "18px" }}
            >
              Line Item Matching Details
            </Typography>
          </div>
        </DialogTitle>

        <DialogContent
          style={{ padding: 0, overflow: "hidden", backgroundColor: "#f8fafc" }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 210px)",
            }}
          >
          <TableContainer
            component={Paper}
            style={{
              maxHeight: 500,
              borderRadius: 0,
              border: "none",
              overflow: "auto",
              boxShadow: "none",
            }}
          >
            <Table
              sx={{
                minWidth: 2000, // Increased width for new columns
                // tableLayout: "fixed",
                borderRight: "1px solid #edeff3ff",
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      left: 0,
                      zIndex: 6,
                      width: stickySerialWidth,
                      minWidth: stickySerialWidth,
                      backgroundColor: "#f8fafc",
                      boxShadow: "2px 0 0 #e2e8f0",
                    }}
                    style={headerCellStyle}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      left: stickySerialWidth,
                      zIndex: 5,
                      width: stickyDescriptionWidth,
                      minWidth: stickyDescriptionWidth,
                      backgroundColor: "#f8fafc",
                      boxShadow: "2px 0 0 #e2e8f0",
                    }}
                    style={headerCellStyle}
                  >
                    Item Description
                  </TableCell>
                  <TableCell style={headerCellStyle}>Part No</TableCell>
                  <TableCell style={headerCellStyle}>Item Code</TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    PO Quantity
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    MRN Quantity
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Invoice Quantity
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Unit Price (Invoice)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (PO)
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    Total Price (Invoice)
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    TAX %
                  </TableCell>
                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CompareArrowsIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Quantity Variance
                      <Tooltip
                        title="PO vs GRN Quantity Variance"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Price Variance
                      <Tooltip
                        title="Unit Price Variance"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  {/* New Status Columns */}
                  {/* <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <InventoryIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Item Status
                      <Tooltip
                        title="Item matching status (PO vs GRN vs Invoice)"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell> */}

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <TrendingUpIcon
                        style={{ fontSize: 16, marginRight: "4px" }}
                      />
                      Quantity Status
                      <Tooltip
                        title="Quantity matching status"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell align="center" style={headerCellStyle}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MoneyIcon style={{ fontSize: 16, marginRight: "4px" }} />
                      Price Status
                      <Tooltip
                        title="Price matching status"
                        arrow
                        slotProps={{ tooltip: { sx: { fontSize: "12px" } } }}
                      >
                        <InfoIcon style={{ fontSize: 16, marginLeft: "4px" }} />
                      </Tooltip>
                    </div>
                  </TableCell>

                  <TableCell
                    align="center"
                    style={headerCellStyle}
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      right: stickyPriceMatchWidth,
                      zIndex: 5,
                      width: stickyQuantityMatchWidth,
                      minWidth: stickyQuantityMatchWidth,
                      backgroundColor: "#f8fafc",
                      borderLeft: "1px solid #e2e8f0",
                      boxShadow: "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                    }}
                  >
                    Quantity Match
                  </TableCell>
                  <TableCell
                    align="center"
                    style={headerCellStyle}
                    sx={{
                      fontWeight: 700,
                      fontSize: "14px",
                      fontFamily: GILROY_BOLD,
                      color: "#334155",
                      position: "sticky",
                      right: 0,
                      zIndex: 6,
                      width: stickyPriceMatchWidth,
                      minWidth: stickyPriceMatchWidth,
                      backgroundColor: "#f8fafc",
                      borderLeft: "1px solid #e2e8f0",
                    }}
                  >
                    Price Match
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {modalData.map((row, idx) => {
                  const rowBackground =
                    idx % 2 === 0 ? "rgba(248,250,252,0.9)" : "#ffffff";

                  return (
                  <TableRow
                    key={idx}
                    sx={{
                      "& td": {
                        py: 1.8,
                        px: 1.5,
                        borderBottom: "1px solid #f1f5f9",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily: GILROY_MEDIUM,
                        fontSize: "14px",
                      },
                      "&:hover td": {
                        backgroundColor: "#eff6ff",
                      },
                    }}
                    style={{
                      backgroundColor: rowBackground,
                    }}
                  >
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: "14px",
                        fontFamily: GILROY_BOLD,
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        left: 0,
                        zIndex: 4,
                        width: stickySerialWidth,
                        minWidth: stickySerialWidth,
                        backgroundColor: rowBackground,
                        boxShadow: "2px 0 0 #e2e8f0",
                      }}
                      style={cellStyle}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "14px",
                        fontFamily: GILROY_MEDIUM,
                        color: "#475569",
                        fontWeight: 500,
                        position: "sticky",
                        left: stickySerialWidth,
                        zIndex: 3,
                        width: stickyDescriptionWidth,
                        minWidth: stickyDescriptionWidth,
                        backgroundColor: rowBackground,
                        boxShadow: "2px 0 0 #e2e8f0",
                      }}
                      style={cellStyle}
                    >
                      {renderDescriptionCell(row.item, idx)}
                    </TableCell>
                    <TableCell style={cellStyle}>{row.partNo || "-"}</TableCell>
                    <TableCell style={cellStyle}>
                      {row.itemCode || "-"}
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.poQty}
                        style={{
                          fontSize: "11px",
                          fontFamily: GILROY_MEDIUM,
                          backgroundColor: "#e3f2fd",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.grnQty ?? row.quantity_received}
                        style={{
                          fontSize: "11px",
                          fontFamily: GILROY_MEDIUM,
                          backgroundColor: "#f3e5f5",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        label={row.invoiceQty}
                        style={{
                          fontSize: "11px",
                          fontFamily: GILROY_MEDIUM,
                          backgroundColor: "#e8f5e9",
                          minWidth: "60px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPricePO)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#bbdefb",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.unitPriceInvoice)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPricePO)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<CurrencyRupeeIcon color="primary" />}
                        label={formatNumber(row.totalPriceInvoice)}
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          poData?.[0]?.line_items?.[0]?.igst_rate
                            ? Math.floor(
                                Number(poData[0].line_items[0].igst_rate)
                              )
                            : invoiceData?.[0]?.line_items?.[0]?.gst_rate
                            ? Math.floor(
                                Number(invoiceData[0].line_items[0].gst_rate)
                              )
                            : "-"
                        }
                        style={{
                          fontSize: "11px",
                          borderColor: "#d1c4e9",
                          color: "rgba(0, 0, 0, 0.87)",
                          minWidth: "70px",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={getVarianceTooltip(row, "poVsGrnQty")}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.poVsGrnQtyVariance)}
                            variant="outlined"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              borderColor:
                                row.poVsGrnQtyVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              color:
                                row.poVsGrnQtyVariance === 0
                                  ? "#2E7D32"
                                  : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={getVarianceTooltip(row, "price")}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Chip
                            size="small"
                            label={formatVariance(row.priceVariance, "price")}
                            variant="outlined"
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              borderColor:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              color:
                                row.priceVariance === 0 ? "#2E7D32" : "#D84315",
                              minWidth: "70px",
                            }}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    {/* New Status Cells
                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Item matching status`}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.itemStatus.status}
                            type={row.itemStatus.type}
                          />
                        </div>
                      </Tooltip>
                    </TableCell> */}

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Quantity status: PO: ${row.poQty}, GRN: ${row.grnQty}, Invoice: ${row.invoiceQty}`}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.quantityStatus.status}
                            type={row.quantityStatus.type}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" style={cellStyle}>
                      <Tooltip
                        title={`Price status: PO: ${row.unitPricePO}, Invoice: ${row.unitPriceInvoice}`}
                        slotProps={{ tooltip: { sx: { fontSize: "10px" } } }}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <ModalStatusChip
                            status={row.priceStatus.status}
                            type={row.priceStatus.type}
                          />
                        </div>
                      </Tooltip>
                    </TableCell>

                    <TableCell
                      align="center"
                      style={cellStyle}
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        right: stickyPriceMatchWidth,
                        zIndex: 4,
                        width: stickyQuantityMatchWidth,
                        minWidth: stickyQuantityMatchWidth,
                        backgroundColor: rowBackground,
                        borderLeft: "1px solid #e2e8f0",
                        boxShadow: "-10px 0 12px -12px rgba(15, 23, 42, 0.35)",
                      }}
                    >
                      {row.poQty === row.grnQty &&
                      row.grnQty === row.invoiceQty ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Run 3-Way Match"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#2E7D32",

                            color: "#2E7D32",
                            "& .MuiChip-icon": { color: "#2E7D32" },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Exception Found"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#D84315",

                            color: "#D84315",
                            "& .MuiChip-icon": { color: "#D84315" },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={cellStyle}
                      sx={{
                        fontSize: "14px",
                        color: "#475569",
                        fontWeight: 600,
                        position: "sticky",
                        right: 0,
                        zIndex: 5,
                        width: stickyPriceMatchWidth,
                        minWidth: stickyPriceMatchWidth,
                        backgroundColor: rowBackground,
                        borderLeft: "1px solid #e2e8f0",
                      }}
                    >
                      {row.totalPricePO === row.totalPriceInvoice ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Run 3-Way Match"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#2E7D32",
                            color: "#2E7D32",
                            "& .MuiChip-icon": { color: "#2E7D32" },
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="Exception Found"
                          size="small"
                          style={{
                            fontSize: "11px",
                            fontWeight: 600,
                            borderColor: "#D84315",
                            color: "#D84315",
                            "& .MuiChip-icon": { color: "#D84315" },
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}

                {modalData.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={19} // Updated colSpan from 16 to 19 for new columns
                      align="center"
                      style={{ padding: "32px 16px" }}
                    >
                      <Typography variant="body2" color="textSecondary">
                        No items to display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>
            <Box
              sx={{
                flexShrink: 0,
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                px: 3,
                py: 2.5,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "1.5fr 1fr" },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f0f7ff",
                    border: "1px solid #dbeafe",
                  }}
                >
                  <ShippingIcon
                    style={{
                      color: "#1976d2",
                      marginTop: "4px",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: "#1976d2",
                        mb: "6px",
                        fontFamily: GILROY_BOLD,
                        fontSize: "18px",
                      }}
                    >
                      Shipping Address
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        fontFamily: GILROY_BOLD,
                      }}
                    >
                      {addressLines[0]}
                    </Typography>
                    {addressLines.slice(1).map((line, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          fontWeight: 400,
                          fontFamily: GILROY_MEDIUM,
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background:
                      "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)",
                    border: "1px solid #dbeafe",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontFamily: GILROY_BOLD,
                      fontWeight: 700,
                      color: "#166534",
                    }}
                  >
                    Grand Total
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: "#64748b",
                          mb: 0.5,
                        }}
                      >
                        PO Total
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 18 }} />
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontFamily: GILROY_BOLD,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          {poData?.[0]?.total_amount
                            ? formatNumber(poData[0].total_amount)
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: "#ffffff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          letterSpacing: "0.04em",
                          textTransform: "uppercase",
                          color: "#64748b",
                          mb: 0.5,
                        }}
                      >
                        Invoice Total
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 18 }} />
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontFamily: GILROY_BOLD,
                            fontWeight: 700,
                            color: "#0f172a",
                          }}
                        >
                          {invoiceData?.[0]?.invoice_amount
                            ? formatNumber(invoiceData[0].invoice_amount)
                            : "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <div
          style={{
            padding: "12px 24px",
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: "#f5f5f5",
          }}
        >
          <Button
            variant="outlined"
            onClick={closeDialog}
            style={{
              borderRadius: "4px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              fontFamily: GILROY_MEDIUM,
            }}
          >
            Close
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default ThreeWayMatchTable;
